import * as React from 'react';
import classnames from 'classnames';
import { AddToCart } from '@grandvisionhq/carts-v2';
import { Tag, UnitPrice, BestPrice } from '@grandvisionhq/common';
import { Text } from '@grandvisionhq/elements';
import { Icon } from '@grandvisionhq/icons';
import { useIsClient } from '@grandvisionhq/lib-utils-react';
import { useProductBlockSlots, ProductReimbursableLogo, ProductDiscountBadge, VariantSwitcher, FormattedLabelLink } from '@grandvisionhq/lib-visuals';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { isDefined } from '@grandvisionhq/utils';
import { ProductRecommendationAction, isContactLenses } from '../types.js';
import { Details } from './Details/Details.js';
import { Images } from './Images/Images.js';
import { Prices } from './Prices/Prices.js';

const ProductBlock = /*#__PURE__*/ React.forwardRef(({ brand , discounted , name , url , productType , activeSku , images , price , isCentAmount =true , pricePerUnit , pricePerLiterFixedDecimals , discountedPrice , variants , offering , promotionLabel , className , onVariantChange , onClick , isInsuranceReimbursable , reimbursableLogos , isReimbursableFiltered , tag , bestPrice , action , tabIndex , productLocation , onAddProduct ,  }, ref)=>{
    const blockId = React.useId();
    const { getLabel , currency  } = useIntl();
    const { isPricePerUnitEnabled , isBestPriceEnabled  } = useFeatureToggles();
    const { Slots  } = useProductBlockSlots();
    const [quantity, setQuantity] = React.useState(1);
    const [productAddedToCart, setProductAddedToCart] = React.useState(false);
    const isClient = useIsClient();
    // This variable is needed because the includes lenses text/label contains a link and this causes a hydration mismatch.
    // Interactive content cannot be nested (a tag within a tag). Reason: both the label for this text
    // and the product block itself are a tags.
    const shouldRenderIncludeLenses = (offering === null || offering === void 0 ? void 0 : offering.includesLenses) && isClient;
    const itemDraft = {
        sku: activeSku,
        quantity,
        type: 'regular'
    };
    if (!activeSku || !isDefined(price) || !url) {
        console.warn(`Cannot render ProductBlock component without a valid price: ${price} and url: ${url} and active sku: ${activeSku}.`);
        return null;
    }
    /*
      Precedence of the labels to show in the product tile header:
      1. If the product has both and a product tag and isReimbursable => only the reimbursable logo is shown
      2. If the product has product tag only => product tag is shown
    */ const headerTag = isInsuranceReimbursable ? /*#__PURE__*/ React.createElement(ProductReimbursableLogo, {
        defaultLogo: reimbursableLogos === null || reimbursableLogos === void 0 ? void 0 : reimbursableLogos.default,
        alternativeLogo: reimbursableLogos === null || reimbursableLogos === void 0 ? void 0 : reimbursableLogos.alternative,
        variant: isReimbursableFiltered ? 'alternative' : 'default'
    }) : typeof (tag === null || tag === void 0 ? void 0 : tag.value) === 'string' && /*#__PURE__*/ React.createElement(Tag, {
        iconKey: tag.key,
        label: tag.value
    });
    const addProduct = ()=>{
        setProductAddedToCart(true);
        onAddProduct && onAddProduct();
    };
    const productBody = /*#__PURE__*/ React.createElement("a", {
        tabIndex: tabIndex,
        ref: ref,
        href: url,
        onClick: onClick,
        "aria-labelledby": `product-block-details__brand-${blockId} product-block-details__name-${blockId} product-block-prices__acting-price-${blockId}`,
        "data-t": "product-block",
        className: classnames('product-block', {
            'product-block__link': action === ProductRecommendationAction.Redirect,
            'product-block--contact-lenses': isContactLenses(productType)
        }, className)
    }, /*#__PURE__*/ React.createElement("aside", {
        className: "product-block__header",
        "data-t": "product-block-header"
    }, headerTag && /*#__PURE__*/ React.createElement("div", {
        className: "product-block__header-left"
    }, headerTag), Slots.WishlistButton ? /*#__PURE__*/ React.createElement("div", {
        className: "product-block__header-right"
    }, /*#__PURE__*/ React.createElement(Slots.WishlistButton, {
        sku: activeSku,
        productLocation: productLocation,
        tabIndex: tabIndex
    })) : null), /*#__PURE__*/ React.createElement("div", {
        className: "product-block__main"
    }, /*#__PURE__*/ React.createElement(Images, {
        front: images.front,
        angle: images.angle
    }), /*#__PURE__*/ React.createElement(Details, {
        brand: brand,
        brandId: `product-block-details__brand-${blockId}`,
        name: name,
        nameId: `product-block-details__name-${blockId}`
    }), /*#__PURE__*/ React.createElement(Prices, {
        price: price,
        discountedPrice: discountedPrice,
        isCentAmount: isCentAmount,
        id: `product-block-prices__acting-price-${blockId}`
    }), discounted && /*#__PURE__*/ React.createElement(ProductDiscountBadge, {
        className: "product-block__discount",
        label: promotionLabel,
        discount: discounted
    }), isPricePerUnitEnabled && (pricePerUnit === null || pricePerUnit === void 0 ? void 0 : pricePerUnit.amount) && (pricePerUnit === null || pricePerUnit === void 0 ? void 0 : pricePerUnit.currencyCode) && /*#__PURE__*/ React.createElement(UnitPrice, {
        className: "product-block__unit-price",
        amount: pricePerUnit === null || pricePerUnit === void 0 ? void 0 : pricePerUnit.amount,
        fixedDecimals: pricePerLiterFixedDecimals,
        currencyCode: pricePerUnit === null || pricePerUnit === void 0 ? void 0 : pricePerUnit.currencyCode,
        isCentAmount: isCentAmount
    }), /*#__PURE__*/ React.createElement(BestPrice, {
        isCentAmount: isCentAmount,
        isFeatureEnabled: isBestPriceEnabled,
        bestPriceAmount: bestPrice,
        currencyCode: currency,
        discountedPrice: discountedPrice,
        originalPrice: price,
        className: 'product-block__best-price'
    })), /*#__PURE__*/ React.createElement("footer", {
        className: "product-block__footer"
    }, !!(variants === null || variants === void 0 ? void 0 : variants.length) && /*#__PURE__*/ React.createElement(VariantSwitcher, {
        variants: variants,
        activeSku: activeSku,
        onVariantChange: onVariantChange,
        className: "product-block__variant-switcher",
        tabIndex: tabIndex
    }), shouldRenderIncludeLenses && /*#__PURE__*/ React.createElement(FormattedLabelLink, {
        className: "product-block__bottom-link",
        labelKey: "products.glasses.offering.includesLenses",
        tabIndex: tabIndex
    })));
    if (action !== ProductRecommendationAction.AddToCart && action !== ProductRecommendationAction.AddQuantity) {
        return productBody;
    } else {
        return /*#__PURE__*/ React.createElement("div", {
            className: "product-block__with-cta"
        }, productBody, productAddedToCart && /*#__PURE__*/ React.createElement("div", {
            className: "product-block__with-cta__success",
            "data-t": "add-to-cart-success-text"
        }, /*#__PURE__*/ React.createElement(Icon, {
            name: "checkmark"
        }), /*#__PURE__*/ React.createElement(Text, {
            className: "product-block__with-cta__success__label"
        }, getLabel('cart.success.addedToCart'))), !productAddedToCart && /*#__PURE__*/ React.createElement(AddToCart, {
            onSuccess: addProduct,
            variant: "ghost",
            caption: getLabel('cart.actions.addToCart'),
            lineItemDrafts: [
                itemDraft
            ],
            handleQuantityUpdate: setQuantity,
            quantity: quantity,
            isQuantitySelectorEnabled: action === 'addQuantity',
            productType: productType
        }));
    }
});

export { ProductBlock, ProductBlock as default };
//# sourceMappingURL=ProductBlock.js.map
